import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Slide,
  Typography,
  useTheme,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CookieIcon from '@mui/icons-material/Cookie';
import { trackEvent, updateAnalyticsStatus } from '../../services/analytics';

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
}

const CookieNotice: React.FC = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cookieSettings, setCookieSettings] = useState<CookieSettings>({
    necessary: true, // Notwendige Cookies sind immer aktiviert
    analytics: true,  // Analytics standardmäßig aktiviert, aber der Benutzer kann sie deaktivieren
  });

  useEffect(() => {
    const hasAccepted = localStorage.getItem('cookiesAccepted');
    if (!hasAccepted) {
      setOpen(true);
    } else {
      // Lade gespeicherte Einstellungen
      const savedSettings = localStorage.getItem('cookieSettings');
      if (savedSettings) {
        setCookieSettings(JSON.parse(savedSettings));
      }
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    setOpen(false);
    
    // Aktualisiere Analytics-Status
    updateAnalyticsStatus();
    
    // Tracke Cookie-Akzeptanz
    trackEvent('Cookies', 'Akzeptiert', 'Alle Cookies');
  };

  const handleDecline = () => {
    const declinedSettings: CookieSettings = {
      necessary: true,
      analytics: false,
    };
    
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookieSettings', JSON.stringify(declinedSettings));
    setCookieSettings(declinedSettings);
    setOpen(false);
    
    // Aktualisiere Analytics-Status
    updateAnalyticsStatus();
    
    // Tracke Cookie-Ablehnung
    trackEvent('Cookies', 'Abgelehnt', 'Nur notwendige Cookies');
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    localStorage.setItem('cookieSettings', JSON.stringify(cookieSettings));
    setSettingsOpen(false);
    setOpen(false);
    
    // Aktualisiere Analytics-Status
    updateAnalyticsStatus();
    
    // Tracke Cookie-Einstellungen
    trackEvent('Cookies', 'Einstellungen angepasst', 
      `Analytics: ${cookieSettings.analytics ? 'Ja' : 'Nein'}`);
  };

  const handleSettingChange = (setting: keyof CookieSettings) => {
    setCookieSettings({
      ...cookieSettings,
      [setting]: !cookieSettings[setting],
    });
  };

  if (!open) return null;

  return (
    <>
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.snackbar,
            backgroundColor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
            backdropFilter: 'blur(8px)',
          }}
        >
          <Box
            sx={{
              maxWidth: 'lg',
              mx: 'auto',
              p: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CookieIcon color="primary" sx={{ fontSize: 24 }} />
              <Typography variant="body2">
                Wir verwenden Cookies und Google Analytics, um Ihnen die bestmögliche Erfahrung auf
                unserer Website zu bieten und unsere Inhalte zu verbessern. Weitere Informationen finden Sie in
                unserer{' '}
                <MuiLink component={RouterLink} to="/privacy">
                  Datenschutzerklärung
                </MuiLink>
                .
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexShrink: 0,
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
              }}
            >
              <Button
                variant="outlined"
                onClick={handleDecline}
                fullWidth={false}
                sx={{ minWidth: 120 }}
              >
                Ablehnen
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenSettings}
                fullWidth={false}
                sx={{ minWidth: 120 }}
              >
                Einstellungen
              </Button>
              <Button
                variant="contained"
                onClick={handleAccept}
                fullWidth={false}
                sx={{ minWidth: 120 }}
              >
                Akzeptieren
              </Button>
            </Box>
          </Box>
        </Box>
      </Slide>

      <Dialog open={settingsOpen} onClose={handleCloseSettings}>
        <DialogTitle>Cookie-Einstellungen</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch checked={cookieSettings.necessary} disabled />
              }
              label="Notwendige Cookies (erforderlich)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
              Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
            </Typography>

            <FormControlLabel
              control={
                <Switch 
                  checked={cookieSettings.analytics} 
                  onChange={() => handleSettingChange('analytics')} 
                />
              }
              label="Google Analytics"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 2 }}>
              Diese Cookies helfen uns, die Nutzung unserer Website zu analysieren und zu verbessern.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSettings}>Abbrechen</Button>
          <Button onClick={handleSaveSettings} variant="contained">Speichern</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieNotice;
